import { useNavigate } from "@tanstack/react-router";
import { Award } from "lucide-react";
import { CertificationCards } from "@/components/certifications/CertificationCards";
import { CertificationFormDialog } from "@/components/certifications/CertificationFormDialog";
import { interactiveRowProps } from "@/components/shared/interactive-row";
import { LibraryListView, useLibraryList } from "@/components/shared/library-list";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { useCertifications } from "@/hooks/use-certifications";
import { carryListSearch } from "@/lib/list-search";
import { formatDate } from "@/lib/utils";

export function CertificationsTable() {
  const navigate = useNavigate();
  const list = useLibraryList("certifications.view");
  const result = useCertifications(list.query, list.pageRequest);

  return (
    <LibraryListView
      list={list}
      result={result}
      noun={{ one: "certification", many: "certifications" }}
      icon={Award}
      headers={[
        { label: "Name", sortKey: "name" },
        { label: "Issuing body", sortKey: "issuingBody", className: "hidden md:table-cell" },
        { label: "Description", sortKey: "description", className: "hidden lg:table-cell" },
        { label: "Status", sortKey: "status" },
        { label: "Added", sortKey: "added", className: "hidden lg:table-cell text-right" },
      ]}
      renderCards={({ items, loading, empty }) => (
        <CertificationCards certifications={items} loading={loading} empty={empty} />
      )}
      renderCreateDialog={({ open, onOpenChange }) => (
        <CertificationFormDialog open={open} onOpenChange={onOpenChange} />
      )}
      renderRow={(c) => (
        <tr
          key={c.id}
          {...interactiveRowProps(
            () =>
              navigate({
                to: "/dashboard/certifications/$certificationId",
                params: { certificationId: c.id },
                search: carryListSearch(),
              }),
            `Open ${c.name}`,
          )}
        >
          <td className="px-4 py-2.5 font-medium text-foreground">{c.name}</td>
          <td className="hidden md:table-cell px-4 py-2.5 text-muted-foreground">
            {c.issuingBody || "—"}
          </td>
          <td className="hidden lg:table-cell px-4 py-2.5 text-muted-foreground">
            <span className="line-clamp-1">{c.description || "—"}</span>
          </td>
          <td className="px-4 py-2.5">
            <LifecycleBadge status={c.status} />
          </td>
          <td className="hidden lg:table-cell px-4 py-2.5 text-right font-mono text-xs text-muted-foreground">
            {formatDate(c.createdAt)}
          </td>
        </tr>
      )}
    />
  );
}
